import { AddressLinkDropdownPreview } from '@Components/AddressLinkDropdownPreview';
import {
    addressColumns,
    costBearerColumns,
    costCenterColumns,
    invoiceColumns,
    ledgerAccountColumns,
    serviceColumns,
    vatColumns,
} from '@Constants/TableColumns';
import {
    FrameEditAddress,
    FrameEditCostBearer,
    FrameEditCostCenter,
    FrameEditLedgerAccount,
    FrameEditService,
    FrameEditVat,
} from '@Modules/Stammdaten';
import {
    DateInput,
    DivWithSpacing,
    Grid,
    GridColumn,
    GridColumns,
    type ISubFrameBodyProps,
    NumberInput,
    SingleSelect,
    TextInput,
    useAutoConfirmed,
    useGuiBinder,
    useLinkedFieldInFrame,
    useLinkProperty,
    useProp,
} from '@synqo/frontend-component-library';
import {
    type ApiAddress,
    type ApiCashJournalPosition,
    type ApiCostBearer,
    type ApiCostCenter,
    type ApiInvoice,
    type ApiLedgerAccount,
    type ApiService,
    type ApiVat,
    EnumCashJournalPositionBookingType,
    enumCashJournalPositionBookingTypeItems,
    EnumFrontendLayoutSetting,
    type EnumProcessState,
    isApproved,
} from '@Model';
import { FrameEditInvoice } from './FrameEditInvoice';
import { LinkedFieldWithLayoutSettings } from '@Components/index';
import { buildPositionElementId } from '@Hooks/Navigation';

function forceReadOnly(
    cashJournalProcessState: EnumProcessState,
    cashJournalPositionProcessState: EnumProcessState,
    name: string,
) {
    if (isApproved(cashJournalProcessState) || isApproved(cashJournalPositionProcessState)) {
        return name !== 'remarks';
    } else {
        return false;
    }
}

export function FrameCashJournalPosition(
    props: ISubFrameBodyProps<ApiCashJournalPosition> & {
        processState: EnumProcessState;
    },
) {
    const pos: ApiCashJournalPosition = props.data;

    const binder = useGuiBinder<ApiCashJournalPosition>({
        ...props,
        forceReadOnly: (name: string) => forceReadOnly(props.processState, pos.process_state, name),
    });

    const linkService = useLinkedFieldInFrame<ApiCashJournalPosition, ApiService>(props, 'service', FrameEditService);
    const linkVat = useLinkedFieldInFrame<ApiCashJournalPosition, ApiVat>(props, 'vat', FrameEditVat);

    const linkLedgerAccount = useLinkedFieldInFrame<ApiCashJournalPosition, ApiLedgerAccount>(
        props,
        'ledger_account',
        FrameEditLedgerAccount,
    );
    const linkCostCenter = useLinkedFieldInFrame<ApiCashJournalPosition, ApiCostCenter>(
        props,
        'cost_center',
        FrameEditCostCenter,
    );
    const linkCostBearer = useLinkedFieldInFrame<ApiCashJournalPosition, ApiCostBearer>(
        props,
        'cost_bearer',
        FrameEditCostBearer,
    );
    const linkInvoice = useLinkedFieldInFrame<ApiCashJournalPosition, ApiInvoice>(
        props,
        'invoice',
        FrameEditInvoice,
        false,
    );
    const linkCreditor = useLinkedFieldInFrame<ApiCashJournalPosition, ApiAddress>(props, 'creditor', FrameEditAddress);

    const bookingTypeField = useAutoConfirmed<ApiCashJournalPosition, EnumCashJournalPositionBookingType>(
        binder,
        'booking_type',
    );
    const dateField = useProp(binder, 'date');
    const priceGrossField = useProp<ApiCashJournalPosition, number>(binder, 'price_gross');
    const priceNetField = useProp<ApiCashJournalPosition, number>(binder, 'price_net');
    const externalInvoiceNumberField = useProp(binder, 'external_invoice_number');
    const remarksField = useProp(binder, 'remarks');
    const serviceField = useLinkProperty(binder, linkService.linkApi);
    const vatField = useLinkProperty(binder, linkVat.linkApi);
    const ledgerAccountField = useLinkProperty(binder, linkLedgerAccount.linkApi);
    const costCenterField = useLinkProperty(binder, linkCostCenter.linkApi);
    const costBearerField = useLinkProperty(binder, linkCostBearer.linkApi);
    const invoiceField = useLinkProperty(binder, linkInvoice.linkApi);
    const creditorField = useLinkProperty(binder, linkCreditor.linkApi);

    function renderEditFields() {
        switch (pos.booking_type) {
            case EnumCashJournalPositionBookingType.Automatic:
                return renderAutomaticBookingFields();
            case EnumCashJournalPositionBookingType.Bank:
                return renderBankBookingFields();
            case EnumCashJournalPositionBookingType.ExternalReceipt:
                return renderExternalBookingFields();
        }
    }

    function renderAutomaticBookingFields() {
        return [
            <GridColumns spans={[4, 4, 4]} key="automatic_booking_1">
                <LinkedFieldWithLayoutSettings<ApiInvoice>
                    layoutSettingsContext={EnumFrontendLayoutSetting.InvoicesTableLayout}
                    {...invoiceField}
                    {...linkInvoice.events}
                    columns={invoiceColumns}
                    label="Rechnung"
                    locked
                />
                <LinkedFieldWithLayoutSettings<ApiCostBearer>
                    layoutSettingsContext={EnumFrontendLayoutSetting.CostBearersTableLayout}
                    {...costBearerField}
                    {...linkCostBearer.events}
                    columns={costBearerColumns}
                    label="Kostenträger"
                    locked
                />
                <LinkedFieldWithLayoutSettings<ApiCostCenter>
                    layoutSettingsContext={EnumFrontendLayoutSetting.CostCentersTableLayout}
                    {...costCenterField}
                    {...linkCostCenter.events}
                    columns={costCenterColumns}
                    label="Kostenstelle"
                    locked
                />
            </GridColumns>,
            <GridColumns spans={[6, 6]} key="automatic_booking_2">
                <LinkedFieldWithLayoutSettings<ApiAddress>
                    layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                    {...creditorField}
                    {...linkCreditor.events}
                    columns={addressColumns}
                    label="Kreditor"
                    dropdownPreview={AddressLinkDropdownPreview}
                    locked
                />
                <LinkedFieldWithLayoutSettings<ApiLedgerAccount>
                    layoutSettingsContext={EnumFrontendLayoutSetting.LedgerAccountsTableLayout}
                    {...ledgerAccountField}
                    {...linkLedgerAccount.events}
                    columns={ledgerAccountColumns}
                    label="Sachkonto"
                    locked
                />
            </GridColumns>,
            <GridColumns spans={[6, 6]} key="automatic_booking_3">
                <NumberInput {...priceGrossField} label="Brutto Preis in €" decimals={2} locked />
                <NumberInput {...priceNetField} decimals={2} label="Netto Preis in €" locked />
            </GridColumns>,
        ];
    }

    function renderBankBookingFields() {
        return [
            <GridColumns spans={[6, 6]} key="bank_booking_1">
                <NumberInput {...priceGrossField} label="Brutto Preis in €" decimals={2} />
                <LinkedFieldWithLayoutSettings<ApiLedgerAccount>
                    layoutSettingsContext={EnumFrontendLayoutSetting.LedgerAccountsTableLayout}
                    {...ledgerAccountField}
                    {...linkLedgerAccount.events}
                    columns={ledgerAccountColumns}
                    label="Sachkonto"
                />
            </GridColumns>,
        ];
    }

    function renderExternalBookingFields() {
        return [
            <GridColumns spans={[4, 4, 4]} key="external_booking_1">
                <NumberInput {...priceGrossField} label="Brutto Preis in €" decimals={2} />
                <NumberInput {...priceNetField} label="Netto Preis in €" decimals={2} />
                <TextInput {...externalInvoiceNumberField} label="Externe Rechnungsnummer" />
            </GridColumns>,
            <GridColumns spans={[4, 4, 4]} key="external_booking_2">
                <LinkedFieldWithLayoutSettings<ApiService>
                    layoutSettingsContext={EnumFrontendLayoutSetting.ServicesTableLayout}
                    {...serviceField}
                    {...linkService.events}
                    columns={serviceColumns}
                    label="Leistung"
                />
                <LinkedFieldWithLayoutSettings<ApiVat>
                    layoutSettingsContext={EnumFrontendLayoutSetting.VatsTableLayout}
                    {...vatField}
                    {...linkVat.events}
                    columns={vatColumns}
                    label="Mehrwertsteuer"
                />
                <LinkedFieldWithLayoutSettings<ApiAddress>
                    layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                    {...creditorField}
                    {...linkCreditor.events}
                    columns={addressColumns}
                    label="Kreditor"
                    dropdownPreview={AddressLinkDropdownPreview}
                />
            </GridColumns>,
            <GridColumns spans={[4, 4, 4]} key="external_booking_3">
                <LinkedFieldWithLayoutSettings<ApiCostBearer>
                    layoutSettingsContext={EnumFrontendLayoutSetting.CostBearersTableLayout}
                    {...costBearerField}
                    {...linkCostBearer.events}
                    columns={costBearerColumns}
                    label="Kostenträger"
                />
                <LinkedFieldWithLayoutSettings<ApiCostCenter>
                    layoutSettingsContext={EnumFrontendLayoutSetting.CostCentersTableLayout}
                    {...costCenterField}
                    {...linkCostCenter.events}
                    columns={costCenterColumns}
                    label="Kostenstelle"
                />
                <LinkedFieldWithLayoutSettings<ApiLedgerAccount>
                    layoutSettingsContext={EnumFrontendLayoutSetting.LedgerAccountsTableLayout}
                    {...ledgerAccountField}
                    {...linkLedgerAccount.events}
                    label="Sachkonto"
                    columns={ledgerAccountColumns}
                />
            </GridColumns>,
        ];
    }

    return (
        <DivWithSpacing
            id={buildPositionElementId(props.data.id)}
            padding={{ left: 'lg', right: 'lg', top: 'md', bottom: 'md' }}
            className={'w-full'}
        >
            <Grid columns={12}>
                <GridColumns spans={[6, 6]}>
                    {pos.booking_type === EnumCashJournalPositionBookingType.Automatic ? (
                        <SingleSelect
                            items={enumCashJournalPositionBookingTypeItems.filter(
                                (item) => item.value === EnumCashJournalPositionBookingType.Automatic,
                            )}
                            {...bookingTypeField}
                            label={'Buchungsart'}
                            locked
                            fullWidth
                        />
                    ) : (
                        <SingleSelect
                            items={enumCashJournalPositionBookingTypeItems.filter(
                                (item) => item.value !== EnumCashJournalPositionBookingType.Automatic,
                            )}
                            {...bookingTypeField}
                            label={'Buchungsart'}
                            fullWidth
                        />
                    )}
                    <DateInput type="day" {...dateField} label="Zahlungsdatum" />
                </GridColumns>
                {...renderEditFields()}
                <GridColumn span={12}>
                    <TextInput {...remarksField} label="Bemerkung" inputSize={3} />
                </GridColumn>
            </Grid>
        </DivWithSpacing>
    );
}
